/**
 * Payment sandbox — the developer console for the EvPay payment APIs (UAT).
 *
 * Fires test collections against the EvPay UAT host and shows the full
 * round-trip: the exact request body, EvPay's response envelope, status, and
 * every signed webhook delivery. Everything lands in DEDICATED test storage —
 * no payment intent, loan, or ledger entry is ever created. Gated client- and
 * server-side on `payment.sandbox`.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "../components/Form.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";
import { formatDateTime } from "../lib/utils.js";

const FS_OPERATORS = ["Mpesa", "TigoPesa", "HaloPesa", "AirtelMoney"] as const;

interface SandboxTxn {
  readonly id: string;
  readonly environment: string;
  readonly operation: string;
  readonly fsOperator: string | null;
  readonly amountMinor: number | null;
  readonly currency: string | null;
  readonly payerAccountId: string | null;
  readonly requestId: string;
  readonly providerTransactionId: string | null;
  readonly requestPayload: unknown;
  readonly responsePayload: unknown;
  readonly authPayload: unknown;
  readonly httpStatus: number | null;
  readonly status: string;
  readonly lastError: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface SandboxWebhook {
  readonly id: string;
  readonly deliveryId: string | null;
  readonly event: string | null;
  readonly orderReference: string | null;
  readonly providerTransactionId: string | null;
  readonly status: string | null;
  readonly signatureValid: boolean;
  readonly headers: Record<string, string>;
  readonly rawBody: string;
  readonly receivedAt: string;
}

interface SandboxView {
  readonly transaction: SandboxTxn;
  readonly webhooks: readonly SandboxWebhook[];
}

interface SandboxConfigParam {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly sensitive: boolean;
}

interface SandboxStatus {
  readonly configured: boolean;
  readonly parameters: readonly SandboxConfigParam[];
}

function statusTone(status: string | null): BadgeTone {
  const s = (status ?? "").toUpperCase();
  if (s === "SUCCESS" || s === "SETTLED") return "success";
  if (s === "FAILED" || s === "REVERSED") return "danger";
  if (s === "PENDING" || s === "PROCESSING") return "warning";
  return "neutral";
}

function Json({ value }: { readonly value: unknown }): JSX.Element {
  return (
    <pre className="max-h-80 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
      {value == null ? "—" : JSON.stringify(value, null, 2)}
    </pre>
  );
}

interface AuthLeg {
  readonly endpoint?: string;
  readonly method?: string;
  readonly url?: string;
  readonly requestHeaders?: Record<string, string>;
  readonly request?: unknown;
  readonly httpStatus?: number;
  readonly response?: unknown;
  readonly authenticated?: boolean;
  readonly error?: string | null;
}

/** A labelled "METHOD url" line + a headers key/value block. */
function RequestLine({
  method,
  url,
  headers,
}: {
  readonly method?: string | undefined;
  readonly url?: string | undefined;
  readonly headers?: Record<string, string> | undefined;
}): JSX.Element | null {
  if (!url && (!headers || Object.keys(headers).length === 0)) return null;
  return (
    <div className="mb-2 space-y-1">
      {url ? (
        <p className="font-mono text-[11px] text-simuflex-navy break-all">
          <span className="font-bold">{method ?? ""}</span> {url}
        </p>
      ) : null}
      {headers && Object.keys(headers).length > 0 ? (
        <div className="rounded-md bg-simuflex-mist/40 px-2 py-1.5">
          {Object.entries(headers).map(([k, v]) => (
            <div key={k} className="font-mono text-[10px] text-simuflex-slate break-all">
              <span className="text-simuflex-navy/70">{k}:</span> {v}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The OAuth token leg — shows whether authentication itself succeeded. */
function AuthSection({ auth }: { readonly auth: unknown }): JSX.Element {
  if (!auth || typeof auth !== "object") {
    return (
      <div>
        <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-simuflex-navy/80">
          Authentication
        </h3>
        <p className="text-xs text-simuflex-slate">No auth exchange captured for this call.</p>
      </div>
    );
  }
  const a = auth as AuthLeg;
  const ok = a.authenticated === true;
  return (
    <div>
      <h3 className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-simuflex-navy/80">
        Authentication
        <Badge tone={ok ? "success" : "danger"}>
          {ok ? "authenticated" : `failed${a.httpStatus ? ` (${a.httpStatus})` : ""}`}
        </Badge>
      </h3>
      {a.error ? (
        <p className="mb-1 text-xs font-semibold text-simuflex-orange">{a.error}</p>
      ) : null}
      <RequestLine method={a.method} url={a.url} headers={a.requestHeaders} />
      <Json value={{ request: a.request, httpStatus: a.httpStatus, response: a.response }} />
    </div>
  );
}

/** Pull {method,url,headers,body} out of a stored request descriptor (loose). */
function asRequestDescriptor(value: unknown): {
  method?: string | undefined;
  url?: string | undefined;
  headers?: Record<string, string> | undefined;
  body?: unknown;
} | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (!("url" in v) && !("headers" in v) && !("body" in v)) return null;
  return {
    method: typeof v["method"] === "string" ? v["method"] : undefined,
    url: typeof v["url"] === "string" ? v["url"] : undefined,
    headers:
      v["headers"] && typeof v["headers"] === "object"
        ? (v["headers"] as Record<string, string>)
        : undefined,
    body: v["body"],
  };
}

export function PaymentSandboxPage(): JSX.Element {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fsOperator, setFsOperator] = useState<string>("Mpesa");
  const [amount, setAmount] = useState("1000");
  const [phone, setPhone] = useState("");
  const [payerName, setPayerName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["sandbox", "status"],
    queryFn: () => api.get<SandboxStatus>("/api/payments/sandbox/status"),
  });
  const txns = useQuery({
    queryKey: ["sandbox", "transactions"],
    queryFn: () => api.get<readonly SandboxTxn[]>("/api/payments/sandbox/transactions"),
  });
  const detail = useQuery({
    queryKey: ["sandbox", "transaction", selectedId],
    queryFn: () => api.get<SandboxView>(`/api/payments/sandbox/transactions/${selectedId}`),
    enabled: selectedId !== null,
  });

  const send = useMutation({
    mutationFn: () =>
      api.post<SandboxView>("/api/payments/sandbox/ussd", {
        fsOperator,
        amountMinor: Number(amount),
        payerAccountId: phone.trim(),
        ...(payerName.trim() ? { payerName: payerName.trim() } : {}),
        currency: "TZS",
      }),
    onSuccess: (res) => {
      setError(null);
      setSelectedId(res.transaction.id);
      void qc.invalidateQueries({ queryKey: ["sandbox", "transactions"] });
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  const refresh = useMutation({
    mutationFn: (id: string) =>
      api.post<SandboxView>(`/api/payments/sandbox/transactions/${id}/refresh`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sandbox", "transaction", selectedId] });
      void qc.invalidateQueries({ queryKey: ["sandbox", "transactions"] });
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  const configured = status.data?.configured ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-simuflex-navy">Payment sandbox</h1>
        <p className="mt-1 text-sm text-simuflex-slate">
          Test the EvPay payment APIs end-to-end against UAT. Requests, responses, and webhooks are
          stored in dedicated test storage — nothing here touches real loans or the ledger.
        </p>
      </div>

      {status.isSuccess && !configured ? (
        <Card>
          <CardBody>
            <p className="text-sm text-simuflex-orange font-semibold">
              The EvPay UAT sandbox is not configured. Set <code>EVMAK_UAT_*</code> (or the base{" "}
              <code>EVMAK_*</code> credentials) and restart the API.
            </p>
          </CardBody>
        </Card>
      ) : null}

      {configured && (status.data?.parameters.length ?? 0) > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
            <Badge tone="success">UAT connected</Badge>
          </CardHeader>
          <CardBody>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {status.data?.parameters.map((p) => (
                <div key={p.key} className="flex items-baseline justify-between gap-3">
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-simuflex-slate">
                    {p.label}
                    {p.sensitive ? (
                      <span className="ml-1 font-normal normal-case text-simuflex-slate/70">
                        (masked)
                      </span>
                    ) : null}
                  </dt>
                  <dd className="font-mono text-xs text-simuflex-navy break-all text-right">
                    {p.value}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-[11px] text-simuflex-slate">
              Client ID, secret, and signing key are masked (last 4 shown). Full values live in the
              API environment only.
            </p>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Send a test push */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Send test USSD push</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                send.mutate();
              }}
            >
              <Field label="Wallet (fsOperator)">
                <Select value={fsOperator} onChange={(e) => setFsOperator(e.target.value)}>
                  {FS_OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Amount" hint="whole TZS, min 100">
                <TextInput
                  type="number"
                  min={100}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Payer phone" hint="UAT test MSISDN">
                <TextInput
                  value={phone}
                  placeholder="255712345678"
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Field>
              <Field label="Payer name" hint="optional">
                <TextInput value={payerName} onChange={(e) => setPayerName(e.target.value)} />
              </Field>
              <FormError message={error} />
              <Button type="submit" disabled={!configured || send.isPending}>
                {send.isPending ? "Sending…" : "Send test push"}
              </Button>
            </form>
          </CardBody>
        </Card>

        {/* Transactions + detail */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Test transactions</CardTitle>
              <span className="text-xs text-simuflex-slate font-semibold">
                {txns.data?.length ?? 0} total · newest first
              </span>
            </CardHeader>
            <CardBody className="p-0">
              {txns.data && txns.data.length > 0 ? (
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <THead>
                      <Tr>
                        <Th>Created</Th>
                        <Th>Wallet</Th>
                        <Th>Amount</Th>
                        <Th>Status</Th>
                        <Th>HTTP</Th>
                        <Th>Request ID</Th>
                      </Tr>
                    </THead>
                    <TBody>
                      {txns.data.map((t) => (
                        <Tr
                          key={t.id}
                          onClick={() => setSelectedId(t.id)}
                          className={
                            selectedId === t.id
                              ? "cursor-pointer bg-simuflex-navy/5"
                              : "cursor-pointer"
                          }
                        >
                          <Td>{formatDateTime(t.createdAt)}</Td>
                          <Td>{t.fsOperator ?? "—"}</Td>
                          <Td>
                            {t.amountMinor != null ? t.amountMinor.toLocaleString() : "—"}{" "}
                            {t.currency}
                          </Td>
                          <Td>
                            <Badge tone={statusTone(t.status)}>{t.status}</Badge>
                          </Td>
                          <Td>{t.httpStatus ?? "—"}</Td>
                          <Td className="font-mono text-[11px]">{t.requestId}</Td>
                        </Tr>
                      ))}
                    </TBody>
                  </Table>
                </div>
              ) : (
                <p className="p-5 text-sm text-simuflex-slate">No test transactions yet.</p>
              )}
            </CardBody>
          </Card>

          {selectedId && detail.data ? (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <CardTitle>Transaction detail</CardTitle>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={refresh.isPending}
                  onClick={() => refresh.mutate(selectedId)}
                >
                  {refresh.isPending ? "Refreshing…" : "Re-poll status"}
                </Button>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4 text-xs text-simuflex-slate">
                    <span>
                      Status{" "}
                      <Badge tone={statusTone(detail.data.transaction.status)}>
                        {detail.data.transaction.status}
                      </Badge>
                    </span>
                    <span>
                      Provider tx:{" "}
                      <span className="font-mono">
                        {detail.data.transaction.providerTransactionId ?? "—"}
                      </span>
                    </span>
                    {detail.data.transaction.lastError ? (
                      <span className="text-simuflex-orange font-semibold">
                        {detail.data.transaction.lastError}
                      </span>
                    ) : null}
                  </div>

                  <AuthSection auth={detail.data.transaction.authPayload} />

                  <div>
                    <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-simuflex-navy/80">
                      USSD request
                    </h3>
                    {(() => {
                      const d = asRequestDescriptor(detail.data.transaction.requestPayload);
                      return d ? (
                        <>
                          <RequestLine method={d.method} url={d.url} headers={d.headers} />
                          <Json value={d.body} />
                        </>
                      ) : (
                        <Json value={detail.data.transaction.requestPayload} />
                      );
                    })()}
                  </div>
                  <div>
                    <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-simuflex-navy/80">
                      USSD response payload
                    </h3>
                    <Json value={detail.data.transaction.responsePayload} />
                  </div>

                  <div>
                    <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-simuflex-navy/80">
                      Webhook deliveries ({detail.data.webhooks.length})
                    </h3>
                    {detail.data.webhooks.length === 0 ? (
                      <p className="text-xs text-simuflex-slate">
                        None received yet. Point EvPay&apos;s UAT webhook at{" "}
                        <code>/api/webhooks/payments/evmak-sandbox/callback</code>.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {detail.data.webhooks.map((w) => (
                          <div key={w.id} className="rounded-lg border border-simuflex-line p-3">
                            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                              <Badge tone={statusTone(w.status)}>{w.status ?? "—"}</Badge>
                              <span className="font-semibold">{w.event ?? "—"}</span>
                              <Badge tone={w.signatureValid ? "success" : "danger"}>
                                {w.signatureValid ? "signature ✓" : "signature ✗"}
                              </Badge>
                              <span className="text-simuflex-slate">
                                {formatDateTime(w.receivedAt)}
                              </span>
                            </div>
                            <Json value={safeParse(w.rawBody)} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}
